import React, { useState, useEffect } from 'react';
import OptimizedImage from '@/components/OptimizedImage';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useIsDesktop } from '@/hooks/use-tablet';
import { UniversalMap } from '@/components/maps';
import { supabase } from '@/integrations/supabase/client';
import { ShareListingButton } from '@/components/ShareListingButton';
import { useFavorites } from '@/hooks/useFavorites';
import { useUnifiedContact } from '@/contexts/UnifiedContactContext';
import { useAuth } from '@/components/AuthProvider';
import { useEffectiveVisibility } from '@/hooks/useEffectiveVisibility';
import { BlurredPrivateData } from '@/components/privacy/BlurredPrivateData';
import { useViewTracking } from '@/hooks/useViewTracking';
import {
  Building2, MapPin, Globe, Clock, DollarSign, Wrench, Shield,
  GraduationCap, Plane, Settings, Briefcase, Truck, Cog, Radio,
// Note: Plane kept only for runway_length_ft context (line 936)
  Check, Mail, Phone, ExternalLink, ChevronLeft, Heart, Share2, MessageCircle,
  Award, Users, AlertTriangle, Zap, Fuel, Home, Cpu, 
  FileCheck, Hammer, Gauge, Calendar, Package, BookOpen, Star, Lock, Camera
} from 'lucide-react';
import { cn } from '@/lib/utils';
import HelicopterSilhouetteIcon from '@/components/icons/HelicopterSilhouetteIcon';
import { ServiceCategory } from '@/types/serviceWizard';
import { getServiceTypeMapPin } from '@/utils/serviceTypeIcons';
import { formatResponseTime, formatTurnaroundTime, formatShiftCoverage, SERVICE_CATEGORY_DISPLAY } from '@/utils/serviceFormatting';
import ServiceCapabilityBadges from '@/components/services/ServiceCapabilityBadges';
import ServiceProductCard from '@/components/services/ServiceProductCard';
import ServiceCourseCard from '@/components/services/ServiceCourseCard';
import ServiceOfferingGrid from '@/components/services/ServiceOfferingGrid';
import ServiceFacilityShowcase from '@/components/services/ServiceFacilityShowcase';
import ServiceOperatingHoursDisplay from '@/components/services/ServiceOperatingHoursDisplay';
import { ServiceOffering } from '@/types/serviceOffering';
import DetailPhotoGallery from '@/components/listings/detail-modal/DetailPhotoGallery';
import RegulatoryApprovalsCard from '@/components/listings/detail-modal/RegulatoryApprovalsCard';
import ServiceTypesCard from '@/components/listings/detail-modal/ServiceTypesCard';
import CoverageRegionsCard from '@/components/listings/detail-modal/CoverageRegionsCard';
import EquipmentListCard from '@/components/listings/detail-modal/EquipmentListCard';
import { SellerSection } from '@/components/listing/SellerSection';

interface ServiceDetailModalProps {
  listing: any;
  isOpen: boolean;
  onClose: () => void;
  onContact?: () => void;
  onNavigate?: () => void;
}


const categoryIcons: Record<ServiceCategory, React.ReactNode> = {
  mro: <Wrench className="w-5 h-5" />,
  camo: <Shield className="w-5 h-5" />,
  part21: <Cog className="w-5 h-5" />,
  training: <GraduationCap className="w-5 h-5" />,
  operator: <HelicopterSilhouetteIcon className="w-5 h-5" />,
  engineering: <Settings className="w-5 h-5" />,
  ground_ops: <Truck className="w-5 h-5" />,
  fbo_heliport: <Building2 className="w-5 h-5" />,
  management: <Briefcase className="w-5 h-5" />,
  uas: <Radio className="w-5 h-5" />
};

interface ServiceProduct {
  id: string;
  name: string;
  product_type: string;
  description: string | null;
  price: number | null;
  currency?: string | null;
  certifications?: string[] | null;
  certification_basis?: string | null;
  photos?: string[] | null;
  compatible_helicopters?: string[] | null;
  part_number?: string | null;
  lead_time?: string | null;
  documentation_url?: string | null;
}

interface ServiceCourse {
  id: string;
  name: string;
  course_type: string;
  duration_value?: number | null;
  duration_unit?: string | null;
  price: number | null;
  currency: string | null;
  description: string | null;
  certification_issued?: string | null;
  syllabus_topics?: string[] | null;
  includes_materials?: boolean | null;
  helicopter_types?: string[] | null;
  max_students?: number | null;
  prerequisites?: string | null;
  documentation_url?: string | null;
}

const ServiceDetailModal: React.FC<ServiceDetailModalProps> = ({
  listing,
  isOpen,
  onClose,
  onContact,
  onNavigate
}) => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const isMobile = !isDesktop;
  const [activeTab, setActiveTab] = useState('overview');
  
  // Track views when modal opens
  useViewTracking(listing?.id || null, isOpen);
  const [products, setProducts] = useState<ServiceProduct[]>([]);
  const [courses, setCourses] = useState<ServiceCourse[]>([]);
  const [offerings, setOfferings] = useState<ServiceOffering[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingOfferings, setLoadingOfferings] = useState(false);
  const [sellerProfile, setSellerProfile] = useState<any>(null);
  
  // Favorites integration
  const { isFavorite, toggleFavorite } = useFavorites();
  const isFavorited = listing ? isFavorite(listing.id) : false;

  // Unified contact integration
  const { openUnifiedContact } = useUnifiedContact();
  const { user } = useAuth();
  const isOwner = user?.id === listing?.user_id;

  // ✅ Privacy visibility hook
  const {
    blurPrice,
    blurLocation,
    blurContact,
  } = useEffectiveVisibility(
    listing?.id,
    listing?.visibility_settings,
    listing?.user_id
  );

  // Compute which fields should be hidden
  const shouldHidePrice = blurPrice;
  const shouldHideLocation = blurLocation;
  const shouldHideContact = blurContact;

  const handleContact = () => {
    if (!listing) return;
    
    const isReq = listing.listing_type === 'service_request' || listing.listing_type === 'rfq_request';
    
    // If not authenticated, show login prompt
    if (!user) {
      window.dispatchEvent(new CustomEvent('require-auth', { 
        detail: { message: 'Please sign in to contact this ' + (isReq ? 'requester' : 'provider') }
      }));
      return;
    }
    
    openUnifiedContact({
      listingId: listing.id,
      listingTitle: listing.title,
      sellerId: listing.user_id,
      assetType: 'services',
      hiddenFields: {
        price: shouldHidePrice,
        location: shouldHideLocation,
        contact: shouldHideContact,
      }
    });
    onClose();
  };

  const requestField = (field: string) => {
    if (!listing) return;
    if (!user) {
      window.dispatchEvent(new CustomEvent('require-auth', {
        detail: { message: 'Please sign in to request this information' }
      }));
      return;
    }
    openUnifiedContact({
      listingId: listing.id,
      listingTitle: listing.title,
      sellerId: listing.user_id,
      assetType: 'services',
      hiddenFields: {
        price: shouldHidePrice,
        location: shouldHideLocation,
        contact: shouldHideContact,
      },
      preselectedFields: [field],
    });
    onClose();
  };

  // Fetch products for Part-21 listings
  useEffect(() => {
    const fetchProducts = async () => {
      if (!listing?.id || listing.service_category !== 'part21') return;
      setLoadingProducts(true);
      try {
        const { data } = await supabase
          .from('service_products')
          .select('*')
          .eq('service_listing_id', listing.id)
          .order('created_at', { ascending: false });
        setProducts(data || []);
      } catch (err) {
        console.error('Error fetching products:', err);
      } finally {
        setLoadingProducts(false);
      }
    };
    if (isOpen) fetchProducts();
  }, [listing?.id, listing?.service_category, isOpen]);

  // Fetch courses for Training listings
  useEffect(() => {
    const fetchCourses = async () => {
      if (!listing?.id || listing.service_category !== 'training') return;
      setLoadingCourses(true);
      try {
        const { data } = await supabase
          .from('service_courses')
          .select('*')
          .eq('service_listing_id', listing.id)
          .order('created_at', { ascending: false });
        setCourses(data || []);
      } catch (err) {
        console.error('Error fetching courses:', err);
      } finally {
        setLoadingCourses(false);
      }
    };
    if (isOpen) fetchCourses();
  }, [listing?.id, listing?.service_category, isOpen]);

  // Fetch universal offerings from service_offerings table
  useEffect(() => {
    const fetchOfferings = async () => {
      if (!listing?.id) return;
      setLoadingOfferings(true);
      try {
        const { data } = await supabase
          .from('service_offerings')
          .select('*')
          .eq('service_listing_id', listing.id)
          .order('display_order');
        
        if (data && data.length > 0) {
          setOfferings(data.map((o: any) => ({
            id: o.id,
            offering_type: o.offering_type,
            name: o.name,
            description: o.description || '',
            price: o.price,
            price_type: o.price_type || 'quote',
            currency: 'USD',
            lead_time: o.lead_time || '',
            photos: o.photos || [],
            documentation_url: o.documentation_url || '',
            compatible_helicopters: o.compatible_helicopters || [],
            is_active: o.is_active ?? true,
            display_order: o.display_order ?? 0,
            category_data: o.category_data || {},
          })));
        } else {
          setOfferings([]);
        }
      } catch (err) {
        console.error('Error fetching offerings:', err);
      } finally {
        setLoadingOfferings(false);
      }
    };
    if (isOpen) fetchOfferings();
  }, [listing?.id, isOpen]);

  // Fetch seller profile
  useEffect(() => {
    const fetchSellerProfile = async () => {
      if (!listing?.user_id) return;
      const { data } = await supabase
        .from('profiles')
        .select('id, user_id, display_name, avatar_url, company_name, company_logo_url, represent_as_company, user_type, is_verified, created_at, dealer_status')
        .eq('user_id', listing.user_id)
        .maybeSingle();
      setSellerProfile(data);
    };
    if (isOpen) fetchSellerProfile();
  }, [listing?.user_id, isOpen]);

  if (!listing) return null;

  const category = listing.service_category as ServiceCategory;
  const isRequest = listing.listing_type === 'service_request' || listing.listing_type === 'rfq_request';
  const serviceTypePin = getServiceTypeMapPin(listing.listing_type);

  const formatPrice = (price?: number, curr?: string) => {
    if (!price) return null;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: curr || 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatListingPrice = () => {
    if (!listing.base_rate || listing.pricing_model === 'quote' || listing.pricing_model === 'contact') {
      return listing.pricing_model === 'quote' ? 'Request Quote' : 'Contact for Pricing';
    }
    const formatted = formatPrice(listing.base_rate, listing.currency);
    return listing.rate_unit ? `${formatted} ${listing.rate_unit}` : formatted;
  };

  // Determine which tabs to show
  const showProducts = category === 'part21' && products.length > 0;
  const showCourses = category === 'training' && courses.length > 0;

  // Category-specific content rendering
  const renderCategorySpecificOverview = () => {
    switch (category) {
      case 'mro':
        return (
          <>
            {listing.aog_support && (
              <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 dark:bg-amber-950/20 dark:border-amber-800">
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-full bg-amber-500/20">
                      <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <p className="font-bold text-amber-700 dark:text-amber-400 text-sm">AOG Support Available 24/7</p>
                      {listing.aog_response_time && (
                        <p className="text-xs text-amber-600 dark:text-amber-500">Response: {listing.aog_response_time}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            {(listing.facility_size_sqm || listing.hangar_capacity) && (
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Home className="w-4 h-4 text-primary" />
                    Facility Details
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {listing.facility_size_sqm && (
                      <div className="text-center p-2.5 bg-muted/50 rounded-lg">
                        <p className="text-xl font-bold text-primary">{listing.facility_size_sqm.toLocaleString()}</p>
                        <p className="text-[10px] text-muted-foreground">Sq. Meters</p>
                      </div>
                    )}
                    {listing.hangar_capacity && (
                      <div className="text-center p-2.5 bg-muted/50 rounded-lg">
                        <p className="text-xl font-bold text-primary">{listing.hangar_capacity}</p>
                        <p className="text-[10px] text-muted-foreground">Aircraft Capacity</p>
                      </div>
                    )}
                    {listing.shift_coverage && (
                      <div className="text-center p-2.5 bg-muted/50 rounded-lg">
                        <p className="text-sm font-bold text-primary">{formatShiftCoverage(listing.shift_coverage)}</p>
                        <p className="text-[10px] text-muted-foreground">Shift Coverage</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        );

      case 'training':
        return (
          <>
            {(listing.instructor_count || listing.simulator_types?.length > 0 || listing.fleet_size) && (
              <Card className="border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2 text-green-700 dark:text-green-400">
                    <GraduationCap className="w-4 h-4" />
                    Training Facility
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    {listing.instructor_count && (
                      <div className="p-2.5 bg-white/60 dark:bg-black/20 rounded-lg">
                        <p className="text-xl font-bold text-green-700 dark:text-green-400">{listing.instructor_count}</p>
                        <p className="text-[10px] text-green-600/80">Instructors</p>
                      </div>
                    )}
                    {listing.fleet_size && (
                      <div className="p-2.5 bg-white/60 dark:bg-black/20 rounded-lg">
                        <p className="text-xl font-bold text-green-700 dark:text-green-400">{listing.fleet_size}</p>
                        <p className="text-[10px] text-green-600/80">Aircraft</p>
                      </div>
                    )}
                    {listing.simulator_types?.length > 0 && (
                      <div className="p-2.5 bg-white/60 dark:bg-black/20 rounded-lg">
                        <p className="text-xl font-bold text-green-700 dark:text-green-400">{listing.simulator_types.length}</p>
                        <p className="text-[10px] text-green-600/80">Simulators</p>
                      </div>
                    )}
                  </div>
                  {listing.simulator_types?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-[10px] text-muted-foreground mb-1.5">Simulator Types</p>
                      <div className="flex flex-wrap gap-1.5">
                        {listing.simulator_types.map((sim: string, idx: number) => (
                          <Badge key={idx} variant="secondary" className="text-[10px]">
                            <Cpu className="w-2.5 h-2.5 mr-1" />
                            {sim}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        );

      case 'operator':
        return (
          <>
            {(listing.fleet_size || listing.service_radius) && (
              <Card className="border-sky-200 bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-950/20 dark:to-blue-950/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2 text-sky-700 dark:text-sky-400">
                    <HelicopterSilhouetteIcon className="w-4 h-4" />
                    Fleet & Coverage
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4 sm:gap-6">
                    {listing.fleet_size && (
                      <div className="text-center">
                        <p className="text-2xl font-bold text-sky-700 dark:text-sky-400">{listing.fleet_size}</p>
                        <p className="text-[10px] text-sky-600/80">Aircraft Fleet</p>
                      </div>
                    )}
                    {listing.service_radius && (
                      <div className="text-center">
                        <p className="text-2xl font-bold text-sky-700 dark:text-sky-400">{listing.service_radius}</p>
                        <p className="text-[10px] text-sky-600/80">km Range</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
            {listing.mission_types?.length > 0 && (
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold">Mission Capabilities</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1.5">
                    {listing.mission_types.map((mission: string, idx: number) => (
                      <Badge key={idx} className="bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400 text-[10px]">
                        {mission}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        );

      case 'fbo_heliport':
        return (
          <>
            {listing.fuel_types?.length > 0 && (
              <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                    <Fuel className="w-4 h-4" />
                    Fuel Services
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1.5">
                    {listing.fuel_types.map((fuel: string, idx: number) => (
                      <Badge key={idx} className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px]">
                        {fuel}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            {listing.amenities?.length > 0 && (
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold">Amenities & Services</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-1.5">
                    {listing.amenities.map((amenity: string, idx: number) => (
                      <div key={idx} className="flex items-center gap-2 text-xs p-2 bg-muted/50 rounded-lg">
                        <Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                        {amenity}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        );

      case 'uas':
        return (
          <>
            {listing.drone_types?.length > 0 && (
              <Card className="border-cyan-200 bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-950/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2 text-cyan-700 dark:text-cyan-400">
                    <Radio className="w-4 h-4" />
                    Drone Capabilities
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1.5">
                    {listing.drone_types.map((drone: string, idx: number) => (
                      <Badge key={idx} className="bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400 text-[10px]">
                        {drone}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            {listing.flight_authorizations?.length > 0 && (
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <FileCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    Flight Authorizations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1.5">
                    {listing.flight_authorizations.map((auth: string, idx: number) => (
                      <Badge key={idx} className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px]">
                        <Check className="w-2.5 h-2.5 mr-1" />
                        {auth}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        );

      case 'engineering':
        return (
          <>
            {listing.software_tools?.length > 0 && (
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Cpu className="w-4 h-4 text-primary" />
                    Software & Tools
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1.5">
                    {listing.software_tools.map((tool: string, idx: number) => (
                      <Badge key={idx} variant="outline" className="text-[10px]">{tool}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            {listing.engineering_specializations?.length > 0 && (
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Hammer className="w-4 h-4 text-primary" />
                    Specializations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1.5">
                    {listing.engineering_specializations.map((spec: string, idx: number) => (
                      <Badge key={idx} variant="secondary" className="text-[10px]">{spec}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        );

      default:
        return null;
    }
  };

  // Shared modal content
  const ModalContent = ({ useNativeScroll = false }: { useNativeScroll?: boolean }) => {
    const content = (
      <div className="pb-32">
        {/* Hero Section */}
        <div className="relative h-44 sm:h-52 md:h-56 shrink-0 overflow-hidden">
          {/* Background - prioritize hero_image for visual impact, show logo as overlay */}
          {listing.hero_image ? (
            // Hero image with optional logo overlay
            <>
              <OptimizedImage
                src={listing.hero_image}
                alt={listing.title}
                className="w-full h-full"
                priority
                size="full"
                variant="hero"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
              {/* Logo overlay for provider listings */}
              {!isRequest && listing.company_logo && (
                <div className="absolute bottom-3 right-3 z-10 bg-white/95 dark:bg-slate-800/95 rounded-lg p-2 shadow-lg backdrop-blur-sm">
                  <OptimizedImage
                    src={listing.company_logo}
                    alt={listing.company_name || listing.title}
                    className="h-10 sm:h-12 w-auto max-w-[120px]"
                    size="thumbnail"
                    priority
                  />
                </div>
              )}
            </>
          ) : !isRequest && listing.company_logo ? (
            // Provider with logo only: clean gradient with centered logo
            <div className="w-full h-full bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-800 dark:via-slate-900 dark:to-slate-950 flex items-center justify-center p-6 sm:p-8">
              <OptimizedImage
                src={listing.company_logo}
                alt={listing.company_name || listing.title}
                className="max-w-[60%] max-h-[65%]"
                size="medium"
                priority
              />
            </div>
          ) : (
            // Fallback: gradient with category icon
            <>
              <div className="w-full h-full bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                <div className="p-6 sm:p-8 rounded-full bg-primary/10">
                  {categoryIcons[category]}
                </div>
              </div>
            </>
          )}
          
          {/* Service Type Pin Icon - top left */}
          <div className="absolute top-3 left-3 z-10">
            <img src={serviceTypePin} alt="" className="w-9 h-9 sm:w-10 sm:h-10"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          </div>
        </div>
        
        {/* Title Info Card */}
        <div className="bg-muted/30 p-4 border-b">
          <div className="bg-background rounded-xl border border-border p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Badge className={cn(
                "text-[10px]",
                isRequest ? "bg-amber-500/90 text-white" : "bg-emerald-500/90 text-white"
              )}>
                {isRequest ? (listing.listing_type === 'rfq_request' ? 'RFQ' : 'Service Request') : SERVICE_CATEGORY_DISPLAY[category]}
              </Badge>
              {listing.featured && (
                <Badge className="bg-amber-500 text-white border-0 text-[10px]">
                  <Star className="w-2.5 h-2.5 mr-1" />
                  Featured
                </Badge>
              )}
            </div>
            <h1 className="text-lg sm:text-xl font-bold text-foreground mb-1">{listing.title}</h1>
            {listing.company_name && (
              <button
                onClick={() => {
                  if (onNavigate) onNavigate();
                  onClose();
                  if (listing.user_id) {
                    navigate(`/user/${listing.user_id}`);
                  }
                }}
                className="text-muted-foreground flex items-center gap-2 hover:text-foreground transition-colors cursor-pointer group text-sm rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Building2 className="w-4 h-4 flex-shrink-0" />
                <span className="group-hover:underline truncate">{listing.company_name}</span>
                <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
              </button>
            )}

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
              {/* Location */}
              {(listing.city || listing.country) && (
                <div className="p-2.5 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-1 text-muted-foreground mb-0.5">
                    <MapPin className="w-3.5 h-3.5" />
                    <span className="text-[10px]">Location</span>
                  </div>
                  <p className="font-semibold text-xs truncate">
                    {[listing.city, listing.country].filter(Boolean).join(', ')}
                  </p>
                </div>
              )}

              {/* Pricing */}
              {shouldHidePrice ? (
                <div className="p-2.5 bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/40 dark:to-green-950/30 rounded-lg border border-emerald-200/60 dark:border-emerald-700/40">
                  <div className="flex items-center gap-1 text-emerald-700 dark:text-emerald-400 mb-0.5">
                    <DollarSign className="w-3.5 h-3.5" />
                    <span className="text-[10px]">Pricing</span>
                  </div>
                  <BlurredPrivateData
                    placeholderContent={<span className="font-semibold text-xs text-emerald-800 dark:text-emerald-300">$X,XXX</span>}
                    fieldName="Pricing"
                    onRequestInfo={isOwner ? undefined : () => requestField('price')}
                    size="sm"
                    inline
                  />
                </div>
              ) : (
                <div className="p-2.5 bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/40 dark:to-green-950/30 rounded-lg border border-emerald-200/60 dark:border-emerald-700/40">
                  <div className="flex items-center gap-1 text-emerald-700 dark:text-emerald-400 mb-0.5">
                    <DollarSign className="w-3.5 h-3.5" />
                    <span className="text-[10px]">Pricing</span>
                  </div>
                  <p className="font-semibold text-xs text-emerald-800 dark:text-emerald-300">{formatListingPrice()}</p>
                </div>
              )}

              {/* Response Time */}
              {listing.response_time && (
                <div className="p-2.5 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/40 dark:to-indigo-950/30 rounded-lg border border-blue-200/60 dark:border-blue-700/40">
                  <div className="flex items-center gap-1 text-blue-700 dark:text-blue-400 mb-0.5">
                    <Zap className="w-3.5 h-3.5" />
                    <span className="text-[10px]">Response</span>
                  </div>
                  <p className="font-semibold text-xs text-blue-800 dark:text-blue-300">{formatResponseTime(listing.response_time)}</p>
                </div>
              )}

              {/* Turnaround */}
              {listing.turnaround_time && (
                <div className="p-2.5 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-1 text-muted-foreground mb-0.5">
                    <Clock className="w-3.5 h-3.5" />
                    <span className="text-[10px]">Turnaround</span>
                  </div>
                  <p className="font-semibold text-xs">{formatTurnaroundTime(listing.turnaround_time)}</p>
                </div>
              )}

              {/* Service Radius */}
              {listing.service_radius && (
                <div className="p-2.5 bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-950/20 dark:to-violet-950/10 rounded-lg border border-indigo-200/50 dark:border-indigo-700/30">
                  <div className="flex items-center gap-1 text-indigo-600 dark:text-indigo-400 mb-0.5">
                    <Gauge className="w-3.5 h-3.5" />
                    <span className="text-[10px] font-medium">Coverage</span>
                  </div>
                  <p className="font-bold text-sm text-indigo-700 dark:text-indigo-300">{listing.service_radius} <span className="text-[10px] font-medium">km</span></p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col">
          <TabsList className="w-full justify-start px-4 h-11 bg-muted/50 rounded-none border-b shrink-0 overflow-x-auto scrollbar-hide after:content-[''] after:w-4 after:flex-shrink-0">
            <TabsTrigger value="overview" className="text-xs sm:text-sm flex-shrink-0">Overview</TabsTrigger>
            {showProducts && <TabsTrigger value="products" className="text-xs sm:text-sm flex-shrink-0">Products</TabsTrigger>}
            {showCourses && <TabsTrigger value="courses" className="text-xs sm:text-sm flex-shrink-0">Courses</TabsTrigger>}
            <TabsTrigger value="capabilities" className="text-xs sm:text-sm flex-shrink-0">Capabilities</TabsTrigger>
            <TabsTrigger value="services" className="text-xs sm:text-sm flex-shrink-0">Services</TabsTrigger>
            <TabsTrigger value="contact" className="text-xs sm:text-sm flex-shrink-0">Contact</TabsTrigger>
          </TabsList>

          <div className="p-4 space-y-4">
            <TabsContent value="overview" className="m-0 space-y-4">
              {/* Category-specific content */}
              {renderCategorySpecificOverview()}

              {/* Photo Gallery - NEW */}
              {listing.photos?.length > 0 && (
                <DetailPhotoGallery 
                  photos={listing.photos} 
                  title="Service Photos"
                />
              )}

              {/* Regulatory Approvals - NEW */}
              <RegulatoryApprovalsCard
                approvals={listing.regulatory_approvals}
                approvalNumbers={listing.approval_numbers}
                certifications={listing.certifications}
              />

              {/* Service Types - NEW */}
              <ServiceTypesCard
                serviceTypes={listing.service_types}
                category={category}
              />

              {/* Coverage Regions - NEW */}
              <CoverageRegionsCard
                regions={listing.coverage_regions}
                serviceRadius={listing.service_radius}
              />

              {/* Equipment List - NEW (for MRO/Ground Ops) */}
              {(category === 'mro' || category === 'ground_ops') && (
                <EquipmentListCard equipment={listing.equipment_list} />
              )}

              {/* Category-Specific Additional Fields */}
              {category === 'part21' && listing.active_stcs_count && (
                <Card className="border-border">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-full bg-amber-100 dark:bg-amber-900/30">
                        <FileCheck className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-amber-700 dark:text-amber-400">{listing.active_stcs_count}</p>
                        <p className="text-xs text-muted-foreground">Active STCs</p>
                      </div>
                      {listing.oem_partnerships?.length > 0 && (
                        <div className="ml-auto">
                          <p className="text-[10px] text-muted-foreground mb-1">OEM Partnerships</p>
                          <div className="flex flex-wrap gap-1">
                            {listing.oem_partnerships.map((oem: string, idx: number) => (
                              <Badge key={idx} variant="secondary" className="text-[10px]">{oem}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Training Aircraft - for training category */}
              {category === 'training' && listing.training_aircraft?.length > 0 && (
                <Card className="border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <HelicopterSilhouetteIcon className="w-4 h-4" />
                      Training Aircraft
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-1.5">
                      {listing.training_aircraft.map((aircraft: string, idx: number) => (
                        <Badge key={idx} variant="outline" className="text-[10px]">{aircraft}</Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* CAMO Managed Aircraft */}
              {category === 'camo' && listing.managed_aircraft?.length > 0 && (
                <Card className="border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <Shield className="w-4 h-4 text-primary" />
                      Managed Aircraft Types
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-1.5">
                      {listing.managed_aircraft.map((aircraft: string, idx: number) => (
                        <Badge key={idx} variant="outline" className="text-[10px]">{aircraft}</Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Management category specifics */}
              {category === 'management' && (
                <>
                  {listing.fleet_under_management && (
                    <Card className="border-border">
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30">
                          <HelicopterSilhouetteIcon className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">{listing.fleet_under_management}</p>
                          <p className="text-xs text-muted-foreground">Aircraft Under Management</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {listing.management_software?.length > 0 && (
                    <Card className="border-border">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2">
                          <Cpu className="w-4 h-4 text-primary" />
                          Management Software
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-1.5">
                          {listing.management_software.map((sw: string, idx: number) => (
                            <Badge key={idx} variant="outline" className="text-[10px]">{sw}</Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}

              {/* UAS category specifics */}
              {category === 'uas' && (
                <>
                  {(listing.payload_capabilities || listing.max_flight_range_km) && (
                    <Card className="border-border">
                      <CardContent className="p-4 grid grid-cols-2 gap-4">
                        {listing.payload_capabilities && (
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-full bg-cyan-100 dark:bg-cyan-900/30">
                              <Package className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                            </div>
                            <div>
                              <p className="text-[10px] text-muted-foreground">Payload</p>
                              <p className="text-sm font-semibold">{listing.payload_capabilities}</p>
                            </div>
                          </div>
                        )}
                        {listing.max_flight_range_km && (
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-full bg-cyan-100 dark:bg-cyan-900/30">
                              <Gauge className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                            </div>
                            <div>
                              <p className="text-[10px] text-muted-foreground">Max Range</p>
                              <p className="text-sm font-semibold">{listing.max_flight_range_km} km</p>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </>
              )}

              {/* FBO category specifics */}
              {category === 'fbo_heliport' && (
                <>
                  {(listing.helipad_count || listing.runway_length_ft) && (
                    <Card className="border-border">
                      <CardContent className="p-4 grid grid-cols-2 gap-4">
                        {listing.helipad_count && (
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-full bg-emerald-100 dark:bg-emerald-900/30">
                              <Home className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                            </div>
                            <div>
                              <p className="text-[10px] text-muted-foreground">Helipads</p>
                              <p className="text-sm font-semibold">{listing.helipad_count}</p>
                            </div>
                          </div>
                        )}
                        {listing.runway_length_ft && (
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-full bg-emerald-100 dark:bg-emerald-900/30">
                              <Plane className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                            </div>
                            <div>
                              <p className="text-[10px] text-muted-foreground">Runway</p>
                              <p className="text-sm font-semibold">{listing.runway_length_ft.toLocaleString()} ft</p>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </>
              )}

              {/* Description */}
              {listing.description && (
                <Card className="border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-primary" />
                      About This Service
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                      {listing.description}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Location Map */}
              {listing.latitude && listing.longitude && (
                <Card className="border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-primary" />
                      Location
                      {shouldHideLocation && <Lock className="w-3 h-3 text-muted-foreground" />}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {shouldHideLocation ? (
                      <BlurredPrivateData
                        placeholderContent={
                          <div className="h-36 sm:h-44 bg-muted rounded-lg flex items-center justify-center">
                            <MapPin className="w-8 h-8 text-muted-foreground/40" />
                          </div>
                        }
                        fieldName="Location"
                        onRequestInfo={isOwner ? undefined : () => requestField('location')}
                      />
                    ) : (
                      <>
                        <div className="h-36 sm:h-44 rounded-lg overflow-hidden">
                          <UniversalMap
                            markers={[{
                              id: listing.id,
                              position: { lat: listing.latitude, lng: listing.longitude },
                              title: listing.title,
                              manufacturer: listing.company_name || 'Service',
                              model: category,
                              listingType: listing.listing_type,
                            }]}
                            height="h-full"
                            mode="interactive"
                            assetType="services"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          {[listing.city, listing.country].filter(Boolean).join(', ')}
                        </p>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Seller / Provider Profile */}
              {sellerProfile && (
                <SellerSection profile={sellerProfile} className="mt-4" onNavigate={() => onClose()} />
              )}
            </TabsContent>

            <TabsContent value="capabilities" className="m-0 space-y-4">
              {/* Helicopter Capabilities */}
              {listing.helicopter_capabilities?.length > 0 && (
                <Card className="border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <HelicopterSilhouetteIcon className="w-4 h-4" />
                      Helicopter Capabilities
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ServiceCapabilityBadges
                      helicopterCapabilityIds={listing.helicopter_capabilities}
                      engineCapabilityIds={listing.engine_capabilities || []}
                      variant="default"
                      maxShow={20}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Engine Capabilities */}
              {listing.engine_capabilities?.length > 0 && (
                <Card className="border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <Cog className="w-4 h-4 text-primary" />
                      Engine Capabilities
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ServiceCapabilityBadges
                      helicopterCapabilityIds={[]}
                      engineCapabilityIds={listing.engine_capabilities}
                      variant="default"
                      maxShow={20}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Engineering Specializations */}
              {listing.engineering_specializations?.length > 0 && (
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Hammer className="w-4 h-4 text-primary" />
                    Engineering Specializations
                  </CardTitle>
                </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-1.5">
                      {listing.engineering_specializations.map((spec: string, idx: number) => (
                        <Badge 
                          key={idx} 
                          variant="secondary" 
                          className={cn(
                            "text-[10px]",
                            spec.toLowerCase().includes('structural') && "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
                            spec.toLowerCase().includes('avionics') && "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
                            spec.toLowerCase().includes('stc') && "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                          )}
                        >
                          {spec}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Software & Tools */}
              {listing.software_tools?.length > 0 && (
                <Card className="border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <Cpu className="w-4 h-4 text-primary" />
                      Software & Tools
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-1.5">
                      {listing.software_tools.map((tool: string, idx: number) => (
                        <Badge key={idx} variant="outline" className="text-[10px] bg-slate-50 dark:bg-slate-800">
                          {tool}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* UAS/Drone Capabilities */}
              {category === 'uas' && (
                <>
                  {listing.drone_types?.length > 0 && (
                    <Card className="border-border">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2">
                          <Radio className="w-4 h-4 text-primary" />
                          Drone Fleet
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-1.5">
                          {listing.drone_types.map((drone: string, idx: number) => (
                            <Badge key={idx} variant="secondary" className="text-[10px] bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400">
                              {drone}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {listing.payload_capabilities?.length > 0 && (
                    <Card className="border-border">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2">
                          <Camera className="w-4 h-4 text-primary" />
                          Payload Capabilities
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-1.5">
                          {listing.payload_capabilities.map((payload: string, idx: number) => (
                            <Badge key={idx} variant="outline" className="text-[10px]">
                              {payload}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {listing.max_flight_range_km && (
                    <Card className="border-border">
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="p-3 rounded-full bg-indigo-100 dark:bg-indigo-900/30">
                          <Gauge className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-indigo-700 dark:text-indigo-400">{listing.max_flight_range_km} <span className="text-sm font-medium">km</span></p>
                          <p className="text-xs text-muted-foreground">Maximum Flight Range</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {listing.flight_authorizations?.length > 0 && (
                    <Card className="border-border">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2">
                          <FileCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                          Flight Authorizations
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-1.5">
                          {listing.flight_authorizations.map((auth: string, idx: number) => (
                            <Badge key={idx} className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px]">
                              <Check className="w-2.5 h-2.5 mr-1" />
                              {auth}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}

              {/* Part-21 OEM Partnerships (standalone when no STCs) */}
              {category === 'part21' && listing.oem_partnerships?.length > 0 && !listing.active_stcs_count && (
                <Card className="border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <Award className="w-4 h-4 text-primary" />
                      OEM Partnerships
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-1.5">
                      {listing.oem_partnerships.map((oem: string, idx: number) => (
                        <Badge key={idx} variant="secondary" className="text-[10px]">{oem}</Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Empty state if no capabilities */}
              {!listing.helicopter_capabilities?.length && 
               !listing.engine_capabilities?.length && 
               !listing.engineering_specializations?.length &&
               !listing.software_tools?.length &&
               !(category === 'uas' && (listing.drone_types?.length || listing.payload_capabilities?.length || listing.flight_authorizations?.length)) &&
               !(category === 'part21' && listing.oem_partnerships?.length) && (
                <Card className="border-border">
                  <CardContent className="p-6 text-center">
                    <Settings className="w-10 h-10 mx-auto text-muted-foreground/40 mb-2" />
                    <p className="text-sm text-muted-foreground">Contact provider for capability details</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="services" className="m-0 space-y-4">
              {/* Use ServiceOfferingGrid for offerings from the database table */}
              {offerings.length > 0 ? (
                <ServiceOfferingGrid
                  offerings={offerings}
                  category={category}
                  formatPrice={formatPrice}
                  onContactForOffering={onContact ? () => onContact() : undefined}
                />
              ) : listing.service_offerings?.length > 0 ? (
                /* Fallback to inline service_offerings from listing (legacy) */
                <div className="space-y-3">
                  {listing.service_offerings.map((offering: any, idx: number) => (
                    <Card key={idx} className="border-border">
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">{offering.name}</p>
                            {offering.description && (
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{offering.description}</p>
                            )}
                          </div>
                          {offering.price && (
                            <Badge variant="secondary" className="text-xs whitespace-nowrap">
                              {formatPrice(offering.price, listing.currency)}
                            </Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="border-border">
                  <CardContent className="p-6 text-center">
                    <Package className="w-10 h-10 mx-auto text-muted-foreground/40 mb-2" />
                    <p className="text-sm text-muted-foreground">Contact provider for service details</p>
                  </CardContent>
                </Card>
              )}

              {/* Facility Showcase - category-specific facility details */}
              <ServiceFacilityShowcase listing={listing} category={category} />
            </TabsContent>

            {showProducts && (
              <TabsContent value="products" className="m-0 space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  {products.map((product) => (
                    <ServiceProductCard
                      key={product.id}
                      product={product}
                      formatPrice={formatPrice}
                    />
                  ))}
                </div>
              </TabsContent>
            )}

            {showCourses && (
              <TabsContent value="courses" className="m-0 space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  {courses.map((course) => (
                    <ServiceCourseCard
                      key={course.id}
                      course={course}
                      formatPrice={formatPrice}
                    />
                  ))}
                </div>
              </TabsContent>
            )}

            <TabsContent value="contact" className="m-0 space-y-4">
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    Contact Information
                    {shouldHideContact && <Lock className="w-3 h-3 text-muted-foreground" />}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {listing.contact_name && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-muted">
                        <Users className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-[10px] text-muted-foreground">Contact Person</p>
                        <p className="font-medium text-sm">{listing.contact_name}</p>
                      </div>
                    </div>
                  )}

                  {listing.contact_email && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-muted">
                        <Mail className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-[10px] text-muted-foreground">Email</p>
                        {shouldHideContact ? (
                          <BlurredPrivateData
                            placeholderContent={<span className="font-medium text-sm">email@hidden.com</span>}
                            fieldName="Email"
                            onRequestInfo={isOwner ? undefined : () => requestField('contact')}
                            size="sm"
                            inline
                          />
                        ) : (
                          <a href={`mailto:${listing.contact_email}`} className="font-medium text-sm text-primary hover:underline">
                            {listing.contact_email}
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {listing.contact_phone && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-muted">
                        <Phone className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-[10px] text-muted-foreground">Phone</p>
                        {shouldHideContact ? (
                          <BlurredPrivateData
                            placeholderContent={<span className="font-medium text-sm">+1 (XXX) XXX-XXXX</span>}
                            fieldName="Phone"
                            onRequestInfo={isOwner ? undefined : () => requestField('contact')}
                            size="sm"
                            inline
                          />
                        ) : (
                          <a href={`tel:${listing.contact_phone}`} className="font-medium text-sm text-primary hover:underline">
                            {listing.contact_phone}
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {listing.website && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-muted">
                        <Globe className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-[10px] text-muted-foreground">Website</p>
                        <a 
                          href={listing.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="font-medium text-sm text-primary hover:underline flex items-center gap-1"
                        >
                          {listing.website}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                  )}

                  <Separator className="my-3" />

                  <Button 
                    className={cn("w-full", isOwner && "bg-muted text-muted-foreground cursor-not-allowed hover:bg-muted")} 
                    size="lg" 
                    onClick={handleContact} 
                    disabled={isOwner}
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    {isOwner ? 'This is your listing' : isRequest ? 'Submit Quote' : 'Contact Provider'}
                  </Button>
                </CardContent>
              </Card>

              {/* Operating Hours */}
              {listing.operating_hours && Object.keys(listing.operating_hours).length > 0 && (
                <ServiceOperatingHoursDisplay 
                  operatingHours={listing.operating_hours}
                  aogSupport={listing.aog_support}
                  aogResponseTime={listing.aog_response_time}
                  shiftCoverage={listing.shift_coverage}
                />
              )}
            </TabsContent>
          </div>
        </Tabs>
      </div>
    );
    
    if (useNativeScroll) {
      return <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">{content}</div>;
    }
    return <ScrollArea className="flex-1 min-h-0">{content}</ScrollArea>;
  };

  // Fixed Bottom CTA
  const BottomCTA = () => (
    <div className="flex-shrink-0 p-3 sm:p-4 border-t bg-background/95 backdrop-blur-sm">
      <div className="flex gap-2 sm:gap-3">
        <Button 
          onClick={handleContact} 
          disabled={isOwner} 
          className={cn("flex-1", isOwner ? "bg-muted text-muted-foreground cursor-not-allowed hover:bg-muted" : "bg-foreground hover:bg-foreground/90 text-background")} 
          size="lg"
        >
          <MessageCircle className="w-4 h-4 mr-2" />
          {isOwner ? 'This is your listing' : isRequest ? 'Submit Quote' : 'Contact Provider'}
        </Button>
        {isRequest && !isOwner && (
          <Button 
            variant="outline"
            size="lg"
            onClick={(e) => {
              e.stopPropagation();
              const event = new CustomEvent('open-service-offer-response', { detail: { request: listing } });
              window.dispatchEvent(event);
            }}
          >
            <DollarSign className="w-4 h-4 mr-2" />
            Offer Service
          </Button>
        )}
        <Button 
          variant="outline" 
          size="lg" 
          className="px-3 sm:px-4"
          onClick={(e) => {
            e.stopPropagation();
            toggleFavorite(listing.id, 'services');
          }}
          aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
        >
          <Heart className={cn("w-4 h-4", isFavorited && "fill-red-500 text-red-500")} />
        </Button>
        <ShareListingButton
          listingId={listing.id}
          title={listing.title}
                price={listing.base_rate || listing.budget_max || 0}
                location={[listing.city, listing.country].filter(Boolean).join(', ')}
                listingType={listing.listing_type}
          assetType="services"
          variant="outline"
          size="default"
          className="px-3 sm:px-4"
        />
      </div>
    </div>
  );

  // Mobile: Full-height Drawer with sticky header (helicopter pattern)
  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()} handleOnly={true}>
        <DrawerContent className="h-[100svh] max-h-[100svh] flex flex-col z-[100]" showCloseButton={false} showHandle={false}>
          {/* Sticky Back Button Header - helicopter pattern */}
          <div className="sticky top-0 z-50 bg-background border-b border-border px-3 py-2.5 flex items-center gap-3 flex-shrink-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-9 w-9 flex-shrink-0 hover:bg-muted"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <DrawerTitle className="sr-only">{isRequest ? 'Service Request Details' : 'Service Provider'}</DrawerTitle>
            <DrawerDescription className="sr-only">
              Service listing for {listing.title}
            </DrawerDescription>
            <span className="font-medium text-sm text-foreground/80 truncate flex-1">
              {isRequest ? 'Service Request' : 'Service Provider'}
            </span>
            <div className="flex items-center gap-1">
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-9 w-9"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFavorite(listing.id, 'services');
                }}
                aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
              >
                <Heart className={cn("w-4 h-4", isFavorited && "fill-red-500 text-red-500")} />
              </Button>
              <ShareListingButton
                listingId={listing.id}
                title={listing.title}
                price={listing.base_rate || listing.budget_max || 0}
                location={[listing.city, listing.country].filter(Boolean).join(', ')}
                listingType={listing.listing_type}
                assetType="services"
                variant="ghost"
                size="icon"
                className="h-9 w-9"
              />
            </div>
          </div>
          
          <ModalContent useNativeScroll />
          <BottomCTA />
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop: Dialog modal
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl h-[90vh] max-h-[90vh] p-0 overflow-hidden flex flex-col">
        <div className="px-6 pt-4 pb-2 border-b flex-shrink-0 flex items-center justify-between">
          <DialogTitle className="text-lg font-semibold">
            {isRequest ? 'Service Request Details' : 'Service Provider'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Service listing for {listing.title}
          </DialogDescription>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                toggleFavorite(listing.id, 'services');
              }}
              aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
            >
              <Heart className={cn("w-4 h-4", isFavorited && "fill-red-500 text-red-500")} />
            </Button>
            <ShareListingButton
              listingId={listing.id}
              title={listing.title}
              price={listing.base_rate || listing.budget_max || 0}
              location={[listing.city, listing.country].filter(Boolean).join(', ')}
              listingType={listing.listing_type}
              assetType="services"
              variant="ghost"
              size="icon"
            />
          </div>
        </div>
        <ModalContent />
        <BottomCTA />
      </DialogContent>
    </Dialog>
  );
};

export default ServiceDetailModal;
